import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Toolbar,
  Stack,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
  Paper,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  keyframes,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/EditOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { Menu } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import {
  listAgents,
  deleteAgent,
  cloneAgent,
  getAgent,
  listWorkflows,
  deleteWorkflow,
  getWorkflow,
} from "../api/agentApi";
import { useUser } from "./UserContext";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

// --- Types ---
interface Agent {
  agent_id: string;
  name: string;
  display_name?: string;
  description: string;
  status: "created" | "ready" | "pending" | "building";
  owner: string;
  version: string;
  isNewClone?: boolean;
}

interface Workflow {
  id: string;
  name: string;
  version: number;
  status?: string;
  current?: boolean;
}

interface Notification {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
}

// --- Animations ---
const pulse = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(33, 150, 243, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

// --- Sub-Components ---
const StatusPulse = ({ status }: { status: string }) => {
  const isRunning = status === "building" || status === "pending";
  const isFailed = status === "failed";
  const color =
    status === "ready"
      ? "#4caf50"
      : isRunning
        ? "#2196f3"
        : isFailed
          ? "#f44336"
          : "#9e9e9e";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
          animation: isRunning ? `${pulse} 2s infinite` : "none",
        }}
      />
    </Box>
  );
};

const AgentInfoCard = ({
  agents,
  currentAgent,
  setAgents,
  setNotification,
  user,
}: any) => {
  const [deleting, setDeleting] = useState("");
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const DESCRIPTION_LIMIT = 120;

  const handleDelete = async (agentId: string) => {
    setDeleting(agentId);
    setDeleteDialogOpen(false);
    try {
      await deleteAgent(agentId);
      setAgents(agents.filter((a: any) => a.agent_id !== agentId));
      setNotification({
        open: true,
        message: "Agent deleted successfully",
        severity: "success",
      });
    } catch (e) {
      setNotification({
        open: true,
        message: "Failed to delete agent. Please try again.",
        severity: "error",
      });
    }
    setDeleting("");
    setAnchorEl(null);
  };

  const handleClone = async (agentId: string) => {
    // Find the source agent
    const sourceAgent = agents.find((a: Agent) => a.agent_id === agentId);
    if (!sourceAgent) return;

    // Create ghost agent for optimistic UI
    const ghostId = `ghost-${Date.now()}`;
    const ghostAgent: Agent = {
      agent_id: ghostId,
      name: `${sourceAgent.name} (Copy)`,
      display_name: sourceAgent.display_name
        ? `${sourceAgent.display_name} (Copy)`
        : `${sourceAgent.name} (Copy)`,
      description: sourceAgent.description || "",
      status: "building",
      owner: sourceAgent.owner,
      version: "1",
      isNewClone: true,
    };

    // Optimistically add ghost card at the beginning
    setAgents([ghostAgent, ...agents]);

    try {
      // Call backend API
      const result = await cloneAgent(agentId);
      const newAgentId = result.agent_id;

      // Add delay to ensure user sees the cloning in progress
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Fetch full agent details
      const fullAgent = await getAgent(newAgentId);

      // Replace ghost with real agent but KEEP isNewClone flag until user chats
      const isStillBuilding =
        fullAgent.status === "building" || fullAgent.status === "pending";
      const hasFailed = fullAgent.status === "failed";

      setAgents((prevAgents: Agent[]) =>
        prevAgents.map((a) =>
          a.agent_id === ghostId
            ? { ...fullAgent, isNewClone: true } // Always keep isNewClone until user chats
            : a,
        ),
      );

      // Show error if clone failed
      if (hasFailed) {
        setNotification({
          open: true,
          message: `Clone failed: ${fullAgent.name}. Check agent details for error information.`,
          severity: "error",
        });
        // Remove failed clone after 5 seconds
        setTimeout(() => {
          setAgents((prevAgents: Agent[]) =>
            prevAgents.filter((a) => a.agent_id !== newAgentId),
          );
        }, 5000);
        return;
      }

      // Poll for status updates if agent is still building
      if (isStillBuilding) {
        const pollInterval = setInterval(async () => {
          try {
            const updatedAgent = await getAgent(newAgentId);
            const stillBuilding =
              updatedAgent.status === "building" ||
              updatedAgent.status === "pending";
            const nowFailed = updatedAgent.status === "failed";

            setAgents((prevAgents: Agent[]) =>
              prevAgents.map((a) =>
                a.agent_id === newAgentId
                  ? { ...updatedAgent, isNewClone: true } // Keep isNewClone until user chats
                  : a,
              ),
            );

            // Handle failure during polling
            if (nowFailed) {
              clearInterval(pollInterval);
              setNotification({
                open: true,
                message: `Clone build failed: ${updatedAgent.name}. Check agent details for error information.`,
                severity: "error",
              });
              // Remove failed clone after 5 seconds
              setTimeout(() => {
                setAgents((prevAgents: Agent[]) =>
                  prevAgents.filter((a) => a.agent_id !== newAgentId),
                );
              }, 5000);
              return;
            }

            // Success - show notification
            if (updatedAgent.status === "ready") {
              clearInterval(pollInterval);
              setNotification({
                open: true,
                message: `Successfully cloned: ${updatedAgent.display_name || updatedAgent.name}`,
                severity: "success",
              });
            }

            // Stop polling when agent is ready or failed
            if (!stillBuilding) {
              clearInterval(pollInterval);
            }
          } catch (error) {
            console.error("Error polling agent status:", error);
            clearInterval(pollInterval);
          }
        }, 3000); // Poll every 3 seconds

        // Clean up interval after 5 minutes
        setTimeout(() => clearInterval(pollInterval), 300000);
      }
    } catch (error) {
      console.error("Clone failed:", error);
      // Remove ghost card on error
      setAgents((prevAgents: Agent[]) =>
        prevAgents.filter((a) => a.agent_id !== ghostId),
      );
      setNotification({
        open: true,
        message: "Failed to clone agent. Please try again.",
        severity: "error",
      });
    }
  };

  const handleEditAction = async () => {
    try {
      const data = await getAgent(currentAgent.agent_id);
      navigate("/agent/create", { state: { agentCard: data } });
    } catch (error) {
      console.error("Failed to load agent details:", error);
      setNotification({
        open: true,
        message: "Failed to load agent details. Please try again.",
        severity: "error",
      });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isTruncated =
    (currentAgent.description || "").length > DESCRIPTION_LIMIT;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: currentAgent.isNewClone ? "2px solid" : "1px solid #cccccc",
        borderColor: currentAgent.isNewClone ? "primary.main" : "#e0e0e0",
        bgcolor: "#ffffff",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        // DIRECTIONAL SHADOW: More shadow on bottom and sides, minimal on top
        boxShadow:
          "0 2px 8px -2px rgba(0,0,0,0.1), 0 6px 12px -2px rgba(0,0,0,0.06)",
        overflow: "hidden",
        "&:hover": {
          borderColor: "primary.main",
          // HOVER: pronounced bottom/side shadow
          boxShadow:
            "0 4px 12px -2px rgba(0,0,0,0.12), 0 12px 24px -4px rgba(0,0,0,0.08)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
        {/* 3. CONTEXTUAL MESSAGE: Show only for new clones */}
        {currentAgent.isNewClone && currentAgent.status === "ready" && (
          <Box
            sx={{
              bgcolor: "rgba(214, 110, 38, 0.08)", // Light version of your brand orange
              px: 1,
              py: 0.25,
              borderRadius: "8px",
              border: "1px dashed",
              borderColor: "#8E588E",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.63rem !important" }}
            >
              Clone complete, you can optionally <strong>edit</strong> before
              your run.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {/* Left side: Avatar + Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
              flex: 1,
            }}
          >
            {/* Avatar with Agent Icon */}
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#6B7280",
                flexShrink: 0,
              }}
            >
              <SmartToyIcon sx={{ fontSize: 20 }} />
            </Avatar>

            {/* Agent Name */}
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: "1.05rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {currentAgent.display_name || currentAgent.name}
            </Typography>
          </Box>

          {/* Right side: Status Pulse */}
          <Box sx={{ flexShrink: 0 }}>
            <StatusPulse status={currentAgent.status} />
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontSize: "0.85rem", lineHeight: 1.6 }}
        >
          {isTruncated
            ? `${(currentAgent.description || "").substring(0, DESCRIPTION_LIMIT)}...`
            : currentAgent.description || ""}
          {isTruncated && (
            <Button
              size="small"
              onClick={() => setOpen(true)}
              sx={{ p: 0, ml: 0.5, textTransform: "none", minWidth: 0 }}
            >
              more
            </Button>
          )}
        </Typography>
      </CardContent>

      {/* THE CONTROL BAR (Integrated Action Footer) */}
      <Box
        sx={{
          m: 1.5,
          mt: 0,
          display: "flex",
          alignItems: "center",
          borderRadius: "8px",
          height: "40px",
        }}
      >
        {currentAgent.isNewClone && currentAgent.status !== "ready" ? (
          /* Building Clone - Show Spinner or Error */
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              gap: 1.5,
            }}
          >
            {currentAgent.status === "failed" ? (
              <>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: "#f44336",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  ✕
                </Box>
                <Typography variant="body2" color="error">
                  Clone failed - removing...
                </Typography>
              </>
            ) : (
              <>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {currentAgent.status === "building"
                    ? "Building agent..."
                    : "Cloning agent..."}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          /* Normal Agent - Show Actions */
          <>
            {/* Chat Button - Left Side */}
            <Button
              component={RouterLink}
              to={`/agent/${currentAgent.agent_id}/chatAgent`}
              disabled={currentAgent.status !== "ready"}
              onClick={() => {
                // Remove isNewClone flag when user clicks Chat
                if (currentAgent.isNewClone) {
                  setAgents((prevAgents: Agent[]) =>
                    prevAgents.map((a) =>
                      a.agent_id === currentAgent.agent_id
                        ? { ...a, isNewClone: false }
                        : a,
                    ),
                  );
                }
              }}
              sx={{
                flexGrow: 1,
                height: "100%",
                justifyContent: "flex-start",
                px: 2,
                textTransform: "none",
                fontWeight: 900,
                fontSize: "1.2rem",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <ChatBubbleOutlineIcon
                sx={{
                  fontSize: "1.8rem !important",
                  color: "primary.main",
                  stroke: "currentColor",
                  strokeWidth: 1.2,
                }}
              />
            </Button>

            {/* Action Icons - Right Side */}
            <Stack direction="row" spacing={1} sx={{ height: "100%", pr: 0.5 }}>
              {/* Clone Action */}
              {user?.role === "admin" && (
                <Tooltip title="Clone">
                  <IconButton
                    size="small"
                    onClick={() => handleClone(currentAgent.agent_id)}
                    // sx={{ color: "primary.main" }}
                  >
                    <ContentCopyIcon
                      sx={{
                        fontSize: "1.4rem !important",
                        stroke: "currentColor",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}

              {/* Edit Action */}
              {user?.role === "admin" && (
                <Tooltip title="Edit">
                  <IconButton onClick={handleEditAction} size="small">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Overflow Menu */}
              {user?.role === "admin" && (
                <>
                  <Tooltip title="More actions">
                    <IconButton
                      size="small"
                      onClick={handleMenuOpen}
                      sx={{ color: "text.secondary" }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      component={RouterLink}
                      to={`/agent/${currentAgent.agent_id}`}
                      onClick={handleMenuClose}
                      sx={{ gap: 1.5, minWidth: 140 }}
                    >
                      <VisibilityIcon fontSize="small" />
                      View
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setDeleteDialogOpen(true);
                        handleMenuClose();
                      }}
                      disabled={deleting === currentAgent.agent_id}
                      sx={{
                        gap: 1.5,
                        color: "#ff4444",
                        "&:hover": { bgcolor: "rgba(255, 68, 68, 0.08)" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                      Delete
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Stack>
          </>
        )}
      </Box>

      {/* Description Dialog */}
      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Agent Description</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {currentAgent.description || ""}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Delete Agent?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete{" "}
            <strong>{currentAgent.display_name || currentAgent.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <Box
          sx={{
            p: 2,
            pt: 0,
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(currentAgent.agent_id)}
            variant="contained"
            color="error"
            disabled={deleting === currentAgent.agent_id}
            startIcon={
              deleting === currentAgent.agent_id ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{ textTransform: "none" }}
          >
            {deleting === currentAgent.agent_id ? "Deleting..." : "Delete"}
          </Button>
        </Box>
      </Dialog>
    </Card>
  );
};

const WorkflowInfoCard = ({
  workflows,
  currentWorkflow,
  setWorkflows,
  setNotification,
  user,
}: any) => {
  const [deleting, setDeleting] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleEditWorkflow = async () => {
    try {
      const workflowData = await getWorkflow(currentWorkflow.id);
      navigate(`/workflow/${currentWorkflow.id}/intent-edit`, {
        state: {
          workflow: workflowData,
        },
      });
    } catch (error) {
      console.error("Failed to load workflow details:", error);
      setNotification({
        open: true,
        message: "Failed to load workflow details. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDelete = async (workflowId: string) => {
    setDeleting(workflowId);
    setDeleteDialogOpen(false);
    try {
      await deleteWorkflow(workflowId);
      setWorkflows(workflows.filter((w: any) => w.id !== workflowId));
      setNotification({
        open: true,
        message: "Workflow deleted successfully",
        severity: "success",
      });
    } catch (e) {
      setNotification({
        open: true,
        message: "Failed to delete workflow. Please try again.",
        severity: "error",
      });
    }
    setDeleting("");
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "row",
        borderRadius: "12px",
        border: "1px solid #cccccc",
        borderColor: "#e0e0e0",
        bgcolor: "#ffffff",
        borderLeft: "2px solid #6B7280",
        // borderLeft: '3px solid #C99364',
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          "0 2px 8px -2px rgba(0,0,0,0.1), 0 6px 12px -2px rgba(0,0,0,0.06)",
        overflow: "hidden",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow:
            "0 4px 12px -2px rgba(0,0,0,0.12), 0 12px 24px -4px rgba(0,0,0,0.08)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* --- Sidebar workflow visual --- */}
      <Box
        sx={{
          width: "54px",
          flexShrink: 0,
          bgcolor: "#F9FAFB",
          borderRight: "1px solid #E5E7EB",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          backgroundSize: "10px 10px",
        }}
      >
        {/* The Connector Line */}
        <Box
          sx={{
            position: "absolute",
            top: 30,
            bottom: 30,
            width: "1.5px",
            bgcolor: "#6B7280",
            opacity: 0.2,
          }}
        />

        <Stack spacing={2.5} sx={{ zIndex: 1, alignItems: "center" }}>
          <Tooltip title="Logic & Conditionals" placement="right">
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "6px",
                bgcolor: "#fff",
                border: "1.5px solid #6B7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
            </Box>
          </Tooltip>

          <Tooltip title="Agentic Execution" placement="right">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                bgcolor: "#6B7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(107, 114, 128, 0.2)",
              }}
            >
              <SmartToyIcon sx={{ fontSize: 18, color: "#fff" }} />
            </Box>
          </Tooltip>

          <Tooltip title="Human Oversight" placement="right">
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "6px",
                bgcolor: "#fff",
                border: "1.5px solid #6B7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssignmentIndIcon sx={{ fontSize: 14, color: "#6B7280" }} />
            </Box>
          </Tooltip>
        </Stack>
      </Box>
      {/* --- END Sidebar workflow visual --- */}

      {/* --- Main content --- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
                flex: 1,
              }}
            >
              {/* <Avatar
                sx={{
                 width: 36,
                height: 36,
                bgcolor: "#6B7280",
                flexShrink: 0,
                }}
              >
                <AccountTreeIcon sx={{ fontSize: 20 }} />
              </Avatar> */}
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {currentWorkflow.name}
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.85rem",
              lineHeight: 1.6,
            }}
          >
            Multi-agent orchestration workflow. Version{" "}
            {currentWorkflow.version}
          </Typography>
        </CardContent>

        {/* THE CONTROL BAR (Integrated Action Footer) */}
        <Box
          sx={{
            m: 1.5,
            mt: 0,
            display: "flex",
            alignItems: "center",
            borderRadius: "8px",
            height: "40px",
          }}
        >
          <Button
            component={RouterLink}
            to={`/workflow/${currentWorkflow.id}/run`}
            sx={{
              flexGrow: 1,
              height: "100%",
              justifyContent: "flex-start",
              px: 2,
              textTransform: "none",
              fontWeight: 900,
              fontSize: "1.2rem",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <PlayArrowIcon
              sx={{
                fontSize: "2.7rem !important",
                color: "primary.main",
                stroke: "currentColor",
                strokeWidth: 1.2,
              }}
            />
          </Button>

          {/* Action Icons - Right Side */}
          <Stack direction="row" spacing={1} sx={{ height: "100%", pr: 0.5 }}>
            {/* Edit Action */}
            {user?.role === "admin" && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => {
                    handleEditWorkflow();
                    handleMenuClose();
                  }}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {user?.role === "admin" && (
              <>
                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    sx={{ color: "text.secondary" }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    component={RouterLink}
                    to={`/workflow/${currentWorkflow.id}`}
                    onClick={handleMenuClose}
                    sx={{ gap: 1.5, minWidth: 140 }}
                  >
                    <VisibilityIcon fontSize="small" />
                    View
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setDeleteDialogOpen(true);
                      handleMenuClose();
                    }}
                    disabled={deleting === currentWorkflow.id}
                    sx={{
                      gap: 1.5,
                      color: "#ff4444",
                      "&:hover": { bgcolor: "rgba(255, 68, 68, 0.08)" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                    Delete
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            Delete Workflow?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete{" "}
              <strong>{currentWorkflow.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <Box
            sx={{
              p: 2,
              pt: 0,
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(currentWorkflow.id)}
              variant="contained"
              color="error"
              disabled={deleting === currentWorkflow.id}
              startIcon={
                deleting === currentWorkflow.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon />
                )
              }
              sx={{ textTransform: "none" }}
            >
              {deleting === currentWorkflow.id ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </Dialog>
      </Box>
    </Card>
  );
};

const AgentDashboard: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [notification, setNotification] = useState<Notification>({
    open: false,
    message: "",
    severity: "info",
  });
  const { user } = useUser();

  useEffect(() => {
    listAgents().then((data) => setAgents(data.agents || []));
    listWorkflows().then((data) => setWorkflows(data || []));
  }, []);

  const handleClose = () => {
    setNotification({ ...notification, open: false });
  };

  const filteredAgents = agents
    .filter((agent) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        agent.name?.toLowerCase().includes(query) ||
        agent.display_name?.toLowerCase().includes(query) ||
        agent.description?.toLowerCase().includes(query);
      const matchesStatus =
        selectedStatus === "all" || agent.status === selectedStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a: any, b: any) => {
      // Sort by date descending (newest first)
      const dateA = new Date(a.date || 0).getTime();
      const dateB = new Date(b.date || 0).getTime();
      return dateB - dateA;
    });

  const filteredWorkflows = workflows
    .filter((wf) => {
      const query = searchQuery.toLowerCase();
      return (
        !searchQuery ||
        wf.name?.toLowerCase().includes(query) ||
        (wf.id && wf.id.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => b.version - a.version);

  return (
    <Box sx={{ pt: 2, px: 3 }}>
      <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4">Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and deploy your autonomous agents.
          </Typography>
        </Box>
      </Toolbar>

      <Paper
        sx={{
          p: 1.5,
          mb: 3,
          mt: 1,
          borderRadius: "12px",
        }}
      >
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ marginTop: "2px" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "8px",
                alignItems: "center",
                display: "flex",
              },
            }}
            sx={{
              "& .MuiInputBase-input": {
                paddingTop: "8px",
                paddingBottom: "8px",
              },
            }}
          />
          {user?.role === "admin" && (
            <>
              <Button
                component={RouterLink}
                to="/agent/create"
                variant="contained"
                startIcon={<SmartToyIcon />}
                disableElevation
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  px: 4.5,
                }}
              >
                Create Agent
              </Button>
              <Button
                component={RouterLink}
                to="/workflow/intent-build"
                variant="contained"
                startIcon={<AccountTreeIcon />}
                disableElevation
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  px: 5.0,
                }}
              >
                Create Workflow
              </Button>
              {/* <Button
              component={RouterLink}
              to="/agent/conversational"
              variant="outlined"
              disableElevation
              sx={{
                borderRadius: "12px",
                borderColor: "primary.main",
                border: "2px solid",
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                px: 4.5,
              }}
            >
              Custom Create
            </Button> */}
            </>
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ borderRadius: "8px", bgcolor: "background.paper" }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="building">Building</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* AGENTS */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 3.6,
        }}
      >
        {filteredAgents.map((agent) => (
          <AgentInfoCard
            key={agent.agent_id}
            agents={agents}
            currentAgent={agent}
            setAgents={setAgents}
            setNotification={setNotification}
            user={user}
          />
        ))}
      </Box>

      {/* WORKFLOWS */}
      <Box sx={{ mt: 6, mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 3.6,
          }}
        >
          {filteredWorkflows.map((wf) => (
            <WorkflowInfoCard
              key={wf.id}
              workflows={workflows}
              currentWorkflow={wf}
              setWorkflows={setWorkflows}
              setNotification={setNotification}
              user={user}
            />
          ))}
        </Box>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AgentDashboard;
